import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';

const Wrap = styled.div`
  width: min(100%, 1800px);
  margin: clamp(12px, 3vw, 28px) auto;
  background: rgba(255, 255, 255, 0.95);
  border-radius: clamp(22px, 3vw, 50px);
  padding: clamp(18px, 2.6vw, 26px);
  box-shadow: 0 4px 6px rgba(241, 134, 13, 0.79);
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const H1 = styled.h1`
  margin: 0;
  font-size: clamp(22px, 2.6vw, 30px);
  line-height: 1.15;
  color: #053969;
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: rgba(0, 0, 0, 0.65);
  font-size: clamp(12.5px, 1.2vw, 14px);
  line-height: 1.45;
`;

/* ✅ ADD: same as Terms */
const LangRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 560px) {
    justify-content: flex-start;
  }
`;

const LangBtn = styled.button`
  border: 1px solid rgba(5, 57, 105, 0.22);
  background: ${(p) => (p.$active ? '#053969' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#053969')};
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 800;
  cursor: pointer;
  transition: 0.15s ease;
  font-size: 13px;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.98;
  }

  &:active {
    transform: translateY(0px);
  }
`;

const Content = styled.div`
  padding-top: clamp(14px, 2.2vw, 20px);
`;

const Toc = styled.nav`
  background: rgba(5, 57, 105, 0.05);
  border: 1px solid rgba(5, 57, 105, 0.1);
  border-radius: 16px;
  padding: 12px 12px;
  margin-bottom: 16px;
`;

const TocTitle = styled.div`
  font-weight: 900;
  color: #053969;
  margin-bottom: 10px;
  font-size: 14px;
`;

const TocList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const TocLink = styled.a`
  display: block;
  text-decoration: none;
  color: rgba(0, 0, 0, 0.78);
  padding: 8px 10px;
  border-radius: 12px;
  font-size: 13px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.7);

  &:hover {
    color: #053969;
    border-color: rgba(5, 57, 105, 0.2);
  }
`;

const Section = styled.section`
  padding: 14px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const H2 = styled.h2`
  margin: 0 0 10px;
  color: #111;
  font-size: clamp(16px, 1.9vw, 20px);
  line-height: 1.2;
`;

const P = styled.p`
  margin: 0 0 10px;
  color: rgba(0, 0, 0, 0.78);
  font-size: clamp(13px, 1.25vw, 14.5px);
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const LinkCard = styled.div`
  border-radius: 16px;
  padding: 12px 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.75);
  display: grid;
  gap: 6px;
`;

const CardTitle = styled.div`
  font-weight: 900;
  color: #053969;
`;

const CardHint = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.45;
`;

const InternalLink = styled(RouterLink)`
  color: rgba(0, 0, 0, 0.82);
  font-weight: 900;
  text-decoration: none;

  &:hover {
    color: #053969;
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid rgba(5, 57, 105, 0.35);
    outline-offset: 3px;
    border-radius: 10px;
  }
`;

const ExternalLink = styled.a`
  color: rgba(0, 0, 0, 0.82);
  font-weight: 900;
  text-decoration: none;

  &:hover {
    color: #053969;
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid rgba(5, 57, 105, 0.35);
    outline-offset: 3px;
    border-radius: 10px;
  }
`;

const Note = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
  font-size: 13px;
  line-height: 1.55;
`;

export default function MapSite() {
  const [lang, setLang] = useState('en');
  const lastUpdated = '2026-01-01';

  const copy = useMemo(() => {
    const base = {
      pageTitle: 'Site Map',
      subtitle: `Last updated: ${lastUpdated}`,
      tocTitle: 'On this page',
      internalDesc: 'Internal navigation links on this website.',
      externalDesc: 'External links to contact and social profiles.',
      note: 'Tip: If you add new pages later, just add them here too — it helps users navigate and looks professional.',
      sections: [
        {
          id: 'main',
          title: '1. Main pages',
          items: [
            { to: '/', label: 'Home', hint: 'Main landing page with services and overview.' },
            {
              to: '/catalog',
              label: 'Catalog',
              hint: 'Templates and offers (ready-to-use options).',
            },
            {
              to: '/blog',
              label: 'Blog',
              hint: 'Useful articles for clients before ordering a website.',
            },
            { to: '/about', label: 'About', hint: 'Who I am and how I work.' },
            {
              to: '/contacts',
              label: 'Contact',
              hint: 'Ways to reach me (Telegram/WhatsApp/Email).',
            },
          ],
        },
        {
          id: 'legal',
          title: '2. Legal',
          items: [
            { to: '/privacy', label: 'Privacy Policy', hint: 'How data and cookies are handled.' },
            {
              to: '/terms',
              label: 'Terms of Service',
              hint: 'Rules of using the website and services.',
            },
          ],
        },
        {
          id: 'social',
          title: '3. Social & messengers',
          external: true,
          items: [
            {
              href: 'https://t.me/const_AlexDev',
              label: 'Telegram',
              hint: 'Fastest way to ask a question.',
            },
            {
              href: 'https://instagram.com/',
              label: 'Instagram',
              hint: 'Updates and portfolio posts.',
            },
            { href: 'https://wa.me/393519741841', label: 'WhatsApp', hint: 'Quick chat.' },
          ],
        },
      ],
    };

    const ru = {
      pageTitle: 'Карта сайта',
      subtitle: `Последнее обновление: ${lastUpdated}`,
      tocTitle: 'Содержание',
      internalDesc: 'Внутренние ссылки для навигации по сайту.',
      externalDesc: 'Внешние ссылки на контакты и социальные профили.',
      note: 'Совет: если позже добавишь новые страницы — просто добавь их сюда. Это помогает пользователям и выглядит профессионально.',
      sections: [
        {
          id: 'main',
          title: '1. Основные страницы',
          items: [
            { to: '/', label: 'Главная', hint: 'Главная страница с услугами и обзором.' },
            { to: '/catalog', label: 'Каталог', hint: 'Шаблоны и предложения (готовые варианты).' },
            {
              to: '/blog',
              label: 'Блог',
              hint: 'Полезные статьи для клиентов перед заказом сайта.',
            },
            { to: '/about', label: 'Обо мне', hint: 'Кто я и как работаю.' },
            {
              to: '/contacts',
              label: 'Контакты',
              hint: 'Как связаться (Telegram/WhatsApp/Email).',
            },
          ],
        },
        {
          id: 'legal',
          title: '2. Юридическая информация',
          items: [
            {
              to: '/privacy',
              label: 'Политика конфиденциальности',
              hint: 'Как обрабатываются данные и cookies.',
            },
            {
              to: '/terms',
              label: 'Условия использования',
              hint: 'Правила использования сайта и услуг.',
            },
          ],
        },
        {
          id: 'social',
          title: '3. Соцсети и мессенджеры',
          external: true,
          items: [
            {
              href: 'https://t.me/const_AlexDev',
              label: 'Telegram',
              hint: 'Самый быстрый способ задать вопрос.',
            },
            {
              href: 'https://instagram.com/',
              label: 'Instagram',
              hint: 'Обновления и посты портфолио.',
            },
            { href: 'https://wa.me/393519741841', label: 'WhatsApp', hint: 'Быстрый чат.' },
          ],
        },
      ],
    };

    const it = {
      pageTitle: 'Mappa del sito',
      subtitle: `Ultimo aggiornamento: ${lastUpdated}`,
      tocTitle: 'Indice',
      internalDesc: 'Link interni di navigazione del sito.',
      externalDesc: 'Link esterni a contatti e profili social.',
      note: 'Suggerimento: se aggiungi nuove pagine in futuro, aggiungile anche qui — aiuta gli utenti e appare più professionale.',
      sections: [
        {
          id: 'main',
          title: '1. Pagine principali',
          items: [
            { to: '/', label: 'Home', hint: 'Landing principale con servizi e panoramica.' },
            { to: '/catalog', label: 'Catalogo', hint: 'Template e offerte (soluzioni pronte).' },
            {
              to: '/blog',
              label: 'Blog',
              hint: 'Articoli utili per i clienti prima di ordinare un sito.',
            },
            { to: '/about', label: 'Chi sono', hint: 'Chi sono e come lavoro.' },
            {
              to: '/contacts',
              label: 'Contatti',
              hint: 'Come contattarmi (Telegram/WhatsApp/Email).',
            },
          ],
        },
        {
          id: 'legal',
          title: '2. Legale',
          items: [
            {
              to: '/privacy',
              label: 'Informativa sulla privacy',
              hint: 'Come vengono gestiti dati e cookie.',
            },
            {
              to: '/terms',
              label: 'Termini di servizio',
              hint: 'Regole per l’uso del sito e dei servizi.',
            },
          ],
        },
        {
          id: 'social',
          title: '3. Social e messenger',
          external: true,
          items: [
            {
              href: 'https://t.me/const_AlexDev',
              label: 'Telegram',
              hint: 'Il modo più veloce per fare una domanda.',
            },
            {
              href: 'https://instagram.com/',
              label: 'Instagram',
              hint: 'Aggiornamenti e post portfolio.',
            },
            { href: 'https://wa.me/393519741841', label: 'WhatsApp', hint: 'Chat veloce.' },
          ],
        },
      ],
    };

    if (lang === 'ru') return ru;
    if (lang === 'it') return it;
    return base;
  }, [lang, lastUpdated]);

  return (
    <Wrap>
      <SEO
        title="Site Map"
        description="Ready-to-use website templates: landing pages, corporate sites, marketplaces."
        path="/sitemap"
      />

      <Header>
        <TitleBlock>
          <H1>{copy.pageTitle}</H1>
          <Sub>{copy.subtitle}</Sub>
        </TitleBlock>

        {/* ✅ LangRow like in Terms */}
        <LangRow>
          <LangBtn type="button" onClick={() => setLang('en')} $active={lang === 'en'}>
            EN
          </LangBtn>
          <LangBtn type="button" onClick={() => setLang('ru')} $active={lang === 'ru'}>
            RU
          </LangBtn>
          <LangBtn type="button" onClick={() => setLang('it')} $active={lang === 'it'}>
            IT
          </LangBtn>
        </LangRow>
      </Header>

      <Content>
        <Toc aria-label="Table of contents">
          <TocTitle>{copy.tocTitle}</TocTitle>
          <TocList>
            {copy.sections.map((s) => (
              <TocLink key={s.id} href={`#${s.id}`}>
                {s.title}
              </TocLink>
            ))}
          </TocList>
        </Toc>

        {copy.sections.map((s) => (
          <Section key={s.id} id={s.id}>
            <H2>{s.title}</H2>
            <P>{s.external ? copy.externalDesc : copy.internalDesc}</P>

            <Grid>
              {s.items.map((it, idx) => (
                <LinkCard key={idx}>
                  <CardTitle>
                    {'href' in it ? (
                      <ExternalLink href={it.href} target="_blank" rel="noreferrer">
                        {it.label}
                      </ExternalLink>
                    ) : (
                      <InternalLink to={it.to}>{it.label}</InternalLink>
                    )}
                  </CardTitle>
                  <CardHint>{it.hint}</CardHint>
                </LinkCard>
              ))}
            </Grid>
          </Section>
        ))}

        <Note>{copy.note}</Note>
      </Content>
    </Wrap>
  );
}
